import React, { useEffect, useState, useMemo, useCallback } from 'react';
import useSWR from 'swr';
import HeaderCard from '../HeaderCard/HeaderCard';
import { IProductList } from 'types/orders';
import { formatRoundedUpNumber } from 'utils/formatNumbers';
import useUsersState from 'state/useUsersState';
import CheckOrAddCommentsModal from 'views/orders/Components/CheckOrAddCommentsModal/CheckOrAddCommentsModal';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import { getOrderDetailedData } from 'services/orders';
import { submitComment } from 'views/LogisticServices/utils/general.utils';
import { getOrganizationData } from 'services/bussines';
import { ICommentsAndObservations } from 'types/global';
import UserWhoCancelledOrder from './components/UserWhoCancelledOrder/UserWhoCancelledOrder';
import './OrderCancelledCard.scss';

/**
 * Componente que muestra la información de una orden cancelada
 * @param data - Datos de la orden cancelada
 * @param refreshData - Función para refrescar los datos después de reactivar la orden
 */
const OrderCancelledCard = ({ data, refreshData }: IOrderCancelledCardProps) => {
  const { users } = useUsersState();
  const { paymentType, responsibleIds, orderId, orderComments, organizationId } = data || {};
  const [showCommentsModal, setShowCommentsModal] = useState<boolean>(false);
  const [loadingComments, setLoadingComments] = useState<boolean>(false);
  const [dataComments, setDataComments] = useState<ICommentsAndObservations[]>();

  const responsibleUsersList = useMemo(() => {
    if (!responsibleIds || !users) return [];
    return users
      .filter(user => responsibleIds?.includes(user?.id))
      .map(user => ({
        id: user?.id,
        user: user,
        orderId: Number(orderId)
      }));
  }, [responsibleIds, users, orderId]);

  const handleOpenOrCloseCommentsModal = () => setShowCommentsModal(!showCommentsModal);

  const { data: organizationInfo } = useSWR(
    showCommentsModal && organizationId ? [`organization-${organizationId}`, organizationId] : null,
    () => getOrganizationData(Number(organizationId)),
    {
      suspense: false,
      revalidateOnFocus: false,
      onError: () => {
        displayErrorNotification();
      }
    }
  );

  const handleCommentsSubmission = async (comment: ICommentsAndObservations) => {
    if (!orderId) return;

    if (organizationId && !organizationInfo) {
      displayErrorNotification({
        customJSXMessage: <>Cargando información de la organización, por favor intenta de nuevo en un momento</>
      });
      return;
    }

    setLoadingComments(true);
    try {
      const kamUserId = organizationInfo?.assignedKam?.id ? Number(organizationInfo.assignedKam.id) : undefined;

      await submitComment({
        orderId,
        allowNewCommentsSubmission: !loadingComments,
        handleLoading: setLoadingComments,
        comments: comment?.comments,
        mentions: comment?.mentions || [],
        commentImage: comment?.commentImage,
        ...(kamUserId && { kamUserId: kamUserId })
      });
      const response = await getOrderDetailedData(orderId);
      setDataComments(response?.orderComments || []);
    } catch (error) {
      displayErrorNotification();
    } finally {
      setLoadingComments(false);
    }
  };

  const handleRefreshData = useCallback(async () => {
    if (!refreshData) return Promise.resolve();
    return Promise.resolve(refreshData());
  }, [refreshData]);

  useEffect(() => {
    setDataComments(orderComments);
  }, [orderComments]);

  const userWhoCancelledOrder = {
    name: 'Blanca Rodriguez',
    profilePhoto: 'https://d2bb2hf1hp3ih3.cloudfront.net/images/Copia%20de%20FotoPerfil_%202-d4e3-c73c.jpg'
  }; // TODO: traer del backend cuando se tenga la información

  return (
    <div className="orderCancelledCard">
      <HeaderCard data={data} openModal={handleOpenOrCloseCommentsModal} isCancelled refreshData={handleRefreshData} />
      <div className="containerMain">
        <div className="containerValues">
          <div className="valueRow">
            <span className="valueLabel">Método de pago:</span>
            <span className="valueAmount">{paymentType}</span>
          </div>
          <div className="valueRow">
            <span className="valueLabel">Monto:</span>
            <span className="valueAmount">
              {`$${
                ['USD', 'ARS', 'BRL'].includes(data?.currency || '')
                  ? formatRoundedUpNumber(Number(data?.totalUsd || 0), 2)
                  : formatRoundedUpNumber(Number(data?.total || 0), 2)
              } ${['BRL', 'ARS'].includes(data?.currency || '') ? 'USD' : data?.currency}`}
            </span>
          </div>
          {!!userWhoCancelledOrder && (
            <div className="valueRow responsibleUsers">
              <span className="valueLabel">Cancelado por:</span>
              <span className="valueAmount">
                <UserWhoCancelledOrder userWhoCancelledOrder={userWhoCancelledOrder} />
              </span>
            </div>
          )}
        </div>
      </div>
      {showCommentsModal && !!organizationId && (
        <CheckOrAddCommentsModal
          closeModal={handleOpenOrCloseCommentsModal}
          orderComments={dataComments}
          handleSubmitComments={handleCommentsSubmission}
          loading={loadingComments}
          responsibleUsers={responsibleUsersList}
          organizationId={organizationId}
        />
      )}
    </div>
  );
};

interface IOrderCancelledCardProps {
  data: IProductList;
  refreshData?: () => Promise<void>;
}
export default OrderCancelledCard;
